import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  InputProps,
  MenuItem,
  MenuProps,
  Select,
  SelectChangeEvent,
  SelectVariants,
  SxProps,
  Typography,
} from '@mui/material'
import React from 'react'

interface SelectOption {
  value: string
  label: string
  color?: string
}

interface SharedSelectProps {
  id?: string
  options?: SelectOption[]
  value?: unknown
  autoWidth?: boolean
  label?: string
  defaultValue?: string
  multiple?: boolean
  displayEmpty?: boolean
  extraSx?: SxProps
  variant?: SelectVariants
  inputProps?: InputProps['inputProps']
  menuProps?: Partial<MenuProps>
  onChange?: (event: SelectChangeEvent<unknown>) => void
  /** Custom render for the selected value display */
  renderValue?: (selected: unknown) => React.ReactNode
  /** Custom render function for each option - receives option and index */
  renderOption?: (option: SelectOption, index: number) => React.ReactNode
  /** Static option component (legacy support) */
  optionComponent?: React.ReactNode
}

const SharedSelect: React.FC<SharedSelectProps> = ({
  id,
  options,
  value,
  autoWidth,
  label,
  defaultValue,
  multiple,
  displayEmpty,
  extraSx,
  variant,
  inputProps,
  menuProps,
  onChange,
  renderValue: customRenderValue,
  renderOption,
  optionComponent,
}) => {
  // Default render for selected value display
  const defaultRenderValue = (selected: unknown) => {
    const selectedOption = options?.find(opt => opt.value === selected || opt.label === selected)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }} gap={1}>
        {selectedOption?.color && (
          <Chip
            size="small"
            style={{
              backgroundColor: selectedOption.color,
              width: 14,
              height: 14,
              borderRadius: '50%',
            }}
          />
        )}
        <Typography variant="subtitle1" noWrap>
          {selectedOption?.label ?? String(selected ?? '')}
        </Typography>
      </Box>
    )
  }

  // Default render for each option
  const defaultRenderOption = (option: SelectOption) => (
    <>
      {option.color && (
        <Chip
          size="small"
          style={{
            backgroundColor: option.color,
            width: 14,
            height: 14,
            borderRadius: '50%',
            marginRight: 8,
          }}
        />
      )}
      <Typography variant="subtitle1">{option.label}</Typography>
    </>
  )

  const labelId = id ? `${id}-label` : 'shared-select-label'

  return (
    <FormControl variant={variant ?? 'standard'} sx={{ minWidth: 120, ...extraSx }}>
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select
        labelId={labelId}
        autoWidth={autoWidth ?? false}
        value={value}
        label={label ?? ''}
        defaultValue={defaultValue}
        multiple={multiple ?? false}
        displayEmpty={displayEmpty ?? false}
        inputProps={inputProps ?? null}
        MenuProps={
          menuProps
            ? menuProps
            : {
                style: { zIndex: 2000 },
              }
        }
        onChange={onChange}
        renderValue={customRenderValue ?? defaultRenderValue}
      >
        {optionComponent
          ? optionComponent
          : options?.map((option, index) => (
              <MenuItem key={option.value} value={option.value}>
                {renderOption ? renderOption(option, index) : defaultRenderOption(option)}
              </MenuItem>
            ))}
      </Select>
    </FormControl>
  )
}

export default SharedSelect
